/*
 * <reviews-media-nav> — bounds the "Next" arrow in the Reviews.io customer-photo
 * lightbox, which Reviews.io never disables. Clicking it on the last photo closes
 * the dialog instead of doing nothing.
 *
 * Behaviour-only element; it renders nothing. Drop it anywhere inside the
 * pdp-reviews section:
 *
 *   <reviews-media-nav></reviews-media-nav>
 *
 * WHAT IS BROKEN, AND WHY IT IS THEIRS NOT OURS
 * ---------------------------------------------
 * polaris/196/9147.build.js, component MediaModal:
 *
 *   shouldShow(e) {
 *     case "previous": if (this.position) return this.index;           // 0 is falsy
 *     case "next":     if (this.position) return null !== this.index;  // ← always true
 *   }
 *
 *   previousReview() { if (this.index - 1 >= 0) emit(…, {index: this.index - 1}) }
 *   nextReview()     {                          emit(…, {index: this.index + 1}) }
 *
 * The `next` guard is vacuous: `this.index` is a number, so `null !== this.index`
 * can never be false and `u-isDisabled` is never applied. `nextReview()` then has
 * no guard of its own either — unlike its sibling — so on the last item it emits
 * an out-of-range index, nothing resolves, `isActive` flips false and the dialog
 * closes. Measured on BABY-SHARK-BOUNCY (14 photo-bearing reviews): next reports
 * enabled at every index 0–13, and the click at 13 closes the modal.
 *
 * Note the `if (this.position)` branch. `position` is set only on the AGGREGATE
 * photo-grid path — `.ElementsWidget__media`, which is how this lightbox opens.
 * The other path (one review's own images) falls through to
 * `media.images[index ± 1]` and is correctly bounded, so this element must not
 * touch it. readIndex() returning -1 is what keeps it out (see there).
 *
 * `u-isDisabled` is COSMETIC ONLY — elements-widget.css gives it
 * `filter: grayscale(1); opacity: .3; cursor: default` and no `pointer-events`.
 * So adding the class is not enough to stop the interaction; the class is the
 * appearance and the capture-phase guard is the behaviour. (It also means the
 * greyed-out `previous` arrow at index 0 is still clickable upstream — harmless,
 * because `previousReview()` does guard.)
 *
 * WHY IT READS THE INDEX RATHER THAN COUNTING CLICKS
 * -------------------------------------------------
 * A mirrored counter drifts the moment anything moves the modal that we did not
 * observe. The DOM already states the answer: the grid thumbnail and the lightbox
 * image are the same asset at different resize params —
 *
 *   grid   …/resize/create?format=jpg&height=220&width=220&src=<asset>
 *   modal  …/resize/create?format=jpg&height=900&width=900&src=<asset>
 *
 * — so the `src` PARAM (not the URL, which is identical for every image on the
 * page) identifies the item, and its position in the grid is the index. That is
 * self-correcting: every sync recomputes from scratch and there is no state to
 * fall out of step. The grid is also the right denominator — it is one thumb per
 * photo-bearing review, which is exactly what the arrows step through.
 *
 * The nth-child caps in section-pdp-reviews.css do NOT change the bound. They
 * `display: none` the tail; the items stay in the DOM and the arrows still reach
 * them, so `total` is the DOM count. (This is also why the layout brief's
 * "capped images become unreachable" note is wrong — they are reachable here.)
 */
if (!customElements.get('reviews-media-nav')) {
  class ReviewsMediaNav extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-type="pdp-reviews"]');
      if (!this.section) return;

      /*
       * Bind to THIS section's modal rather than the first one on the page.
       * build.js derives the teleport host id from the widget selector it was
       * initialised with, stripping every non-alphanumeric character:
       *   "reviews-io-polaris-modal-" + selector.replace(/[^a-z0-9]/gi, "")
       * The leading "#" is stripped either way, so the element id alone
       * reproduces it. Falls back to the prefix if the widget markup moves.
       */
      const widget = this.section.querySelector('[data-reviewsio-widget="polaris"]');
      this.hostId = widget && widget.id
        ? `reviews-io-polaris-modal-${widget.id.replace(/[^a-z0-9]/gi, '')}`
        : null;

      this.onEvent = this.onEvent.bind(this);
      /* Capture phase, on document: the host is a child of <body>, outside this
         section, and Vue 3 binds its handlers straight to the control — so
         stopping the event on the way DOWN is the only place it can be caught
         before their listener runs. */
      document.addEventListener('click', this.onEvent, true);
      document.addEventListener('keyup', this.onEvent, true);

      /* The host is created at widget init, which may be before or after this
         element connects, so watch <body> for it and only then watch inside it.
         childList on body is not subtree — the host is appended directly. */
      this.bodyObserver = new MutationObserver(() => this.attach());
      this.bodyObserver.observe(document.body, { childList: true });
      this.attach();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onEvent, true);
      document.removeEventListener('keyup', this.onEvent, true);
      this.bodyObserver?.disconnect();
      this.hostObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    get host() {
      return this.hostId
        ? document.getElementById(this.hostId)
        : document.querySelector('[id^="reviews-io-polaris-modal-"]');
    }

    attach() {
      const host = this.host;
      if (!host || host === this.watched) return;
      this.hostObserver?.disconnect();
      this.watched = host;
      /* attributeFilter includes 'class' because the open/close state and the
         arrow state both live in class lists. This observer therefore sees its
         own writes; sync() compares before writing, so the pass triggered by a
         write finds nothing to change — one extra callback, not a loop. Same
         termination argument as reviews-rating-bars.js. */
      this.hostObserver = new MutationObserver(this.schedule.bind(this));
      this.hostObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'src'],
      });
      this.sync();
    }

    /* Coalesce a burst of mutations (opening the dialog touches the whole
       subtree) into one pass on the next frame. */
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    /* The two arrows are the only role="button" controls in the dialog — the
       close control carries neither role nor tabindex, and the share row is
       plain links. DOM order is prev, next. Matched on that rather than on
       `title`, which Reviews.io runs through its own translation layer. */
    arrows() {
      const host = this.host;
      const modal = host && host.querySelector('.R-GlobalModal.isActive');
      if (!modal) return null;
      const btns = [...modal.querySelectorAll('.controls__button[role="button"]')];
      return btns.length >= 2 ? { prev: btns[0], next: btns[1] } : null;
    }

    /* Grid thumbnails, in DOM order — including any the nth-child caps hide. */
    thumbs() {
      return [...this.section.querySelectorAll('.R-MediaSlider__item img')];
    }

    /* The `src` query param, which is the underlying asset URL. Returns null for
       anything that is not one of these resize URLs, so a video review (no
       .R-ImageContainer__src at all) or a differently-served image simply fails
       to match and leaves Reviews.io's own behaviour alone. */
    assetOf(el) {
      const raw = el && el.getAttribute('src');
      if (!raw) return null;
      try {
        return new URL(raw, window.location.href).searchParams.get('src');
      } catch {
        return null;
      }
    }

    /*
     * -1 means "not the aggregate-grid path, or not resolvable" — a video
     * review, or the lightbox opened from one review's own image strip, where
     * Reviews.io's bounds check is already correct. Every caller treats -1 as
     * "do nothing", which is what keeps this element off that path.
     */
    readIndex() {
      const host = this.host;
      const img = host && host.querySelector('.R-ImageContainer__src');
      const asset = this.assetOf(img);
      if (!asset) return -1;
      return this.thumbs().findIndex((t) => this.assetOf(t) === asset);
    }

    atLastItem() {
      const total = this.thumbs().length;
      if (total === 0) return false;
      const index = this.readIndex();
      return index !== -1 && index === total - 1;
    }

    sync() {
      const arrows = this.arrows();
      if (!arrows) return;
      const disable = this.atLastItem();

      /* Compare before writing — this is what stops the observer feeding itself. */
      if (arrows.next.classList.contains('u-isDisabled') !== disable) {
        arrows.next.classList.toggle('u-isDisabled', disable);
      }
      /* aria-disabled rather than removing the control: it stays in the tab order
         and is announced as unavailable, which is the WCAG-preferred treatment
         for a control that is present but inert. Reviews.io sets no ARIA here. */
      if ((arrows.next.getAttribute('aria-disabled') === 'true') !== disable) {
        if (disable) arrows.next.setAttribute('aria-disabled', 'true');
        else arrows.next.removeAttribute('aria-disabled');
      }
    }

    onEvent(event) {
      /* keyup: Reviews.io binds enter/space on the control itself, so a keyboard
         user reaches nextReview() without a click ever happening. */
      if (event.type === 'keyup' && event.key !== 'Enter' && event.key !== ' ') return;

      const arrows = this.arrows();
      if (!arrows) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !arrows.next.contains(target)) return;
      if (!this.atLastItem()) return;

      /* Recomputed here rather than trusting the class sync() last wrote: a
         re-render between the two would otherwise decide this. */
      event.stopPropagation();
      event.preventDefault();
    }
  }

  customElements.define('reviews-media-nav', ReviewsMediaNav);
}
